import React, {useEffect, useState} from 'react'

import {RechargeDialog} from '@/app/modules/purchases/RechargeDialog'
import {useSubscription} from '@/shared/utils/SubscriptionProvider'

/**
 * Cloud-units notice inside the composer card: a warning under one million units, and the
 * "new messages are paused" lock at zero. The chat itself (tabs, history, running turns) stays
 * usable either way; only starting a new turn is blocked.
 */
export const ComposerCloudUnitsBanner: React.FC = () => {
  const {balance, canUseAiChat, cloudUnitsStatus, refreshBalance, role, subscriptionId} =
    useSubscription()
  const [showRecharge, setShowRecharge] = useState(false)
  const isEmpty = cloudUnitsStatus === 'empty'

  // Recharging happens in a Stripe tab; re-read the balance when the user comes back so the
  // composer unlocks without a page reload.
  useEffect(() => {
    if (!isEmpty) return
    const onFocus = () => void refreshBalance()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [isEmpty, refreshBalance])

  // Without full chat access (read-only, expired subscription) ComposerSubscriptionBanner explains
  // the lock instead; recharging units would not unlock it.
  if (!canUseAiChat || (cloudUnitsStatus !== 'low' && !isEmpty)) {
    return null
  }

  return (
    <>
      <div
        className={`aui-cloud-units-banner aui-cloud-units-banner--${cloudUnitsStatus}`}
        role={isEmpty ? 'alert' : 'status'}
        data-status={cloudUnitsStatus}
        data-testid='omics-ai-chat-cloud-units-banner'
      >
        <i className={isEmpty ? 'fas fa-circle-exclamation' : 'fas fa-battery-quarter'} aria-hidden />
        <span className='aui-cloud-units-banner__text'>
          {isEmpty
            ? 'No cloud units left — new messages are paused.'
            : `Low on cloud units: ${(balance ?? 0).toLocaleString()} left.`}
        </span>
        <button
          type='button'
          className='aui-cloud-units-banner__action'
          onClick={() => setShowRecharge(true)}
        >
          Add cloud units
        </button>
      </div>
      {showRecharge && (
        <RechargeDialog
          closeModal={() => setShowRecharge(false)}
          role={role}
          subscriptionId={subscriptionId}
        />
      )}
    </>
  )
}
